import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import type { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  invokeEdgeFunction,
  isDriverInAppSourceRow,
  isSafeSqlIdent,
  slugifyReminderCode,
  supabase,
  usePortalPermissions,
  type ReminderCategory,
  type ReminderChannel,
  type ReminderConfig,
  type ReminderRuleKind,
  type ReminderSourceFieldRow,
  type ReminderType,
} from '@ubus/shared';
import AdminListPage from '../components/AdminListPage';
import { localeConfirm } from '../utils/localeModal';
import { formatAdminDbError } from '../utils/formatAdminDbError';

const { Text, Paragraph } = Typography;

const CHANNELS: ReminderChannel[] = ['in_app_admin', 'in_app_driver', 'email', 'sms'];

type ConfigRow = ReminderConfig & {
  reminder_types?: ReminderType | ReminderType[] | null;
};

type ScanResult = {
  ok?: boolean;
  eventsCreated?: number;
  deliveriesCreated?: number;
  error?: string;
};

function typeOf(row: ConfigRow): ReminderType | null {
  const t = row.reminder_types;
  if (!t) return null;
  return Array.isArray(t) ? t[0] ?? null : t;
}

function typeCodeFromSourceField(sourceField: string): string {
  const base = sourceField
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 72);
  return base || slugifyReminderCode('type');
}

function ReminderSettings() {
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePortalPermissions();
  const canManage = hasPermission('reminders.manage');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [savingCode, setSavingCode] = useState<string | null>(null);
  const [rows, setRows] = useState<ConfigRow[]>([]);
  const [editing, setEditing] = useState<ConfigRow | null>(null);
  const [form] = Form.useForm();
  const [sourceFields, setSourceFields] = useState<ReminderSourceFieldRow[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourceModalOpen, setSourceModalOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<ReminderSourceFieldRow | null>(null);
  const [savingSource, setSavingSource] = useState(false);
  const [sourceForm] = Form.useForm();

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('reminder_configs')
        .select('*, reminder_types(*)')
        .order('type_code');
      if (error) throw error;
      const sorted = ((data || []) as ConfigRow[]).sort((a, b) => {
        const ao = typeOf(a)?.sort_order ?? 0;
        const bo = typeOf(b)?.sort_order ?? 0;
        return ao - bo;
      });
      setRows(sorted);
    } catch (err) {
      message.error(err instanceof Error ? err.message : t('reminders.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);


  const loadSources = useCallback(async () => {
    setSourcesLoading(true);
    try {
      const { data, error } = await supabase
        .from('reminder_source_fields')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      setSourceFields((data || []) as ReminderSourceFieldRow[]);
    } catch (err) {
      message.error(err instanceof Error ? err.message : t('reminders.sourcesLoadFailed'));
    } finally {
      setSourcesLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadConfigs();
    void loadSources();
  }, [loadConfigs, loadSources]);

  const runScan = async () => {
    setScanning(true);
    try {
      const { data, error } = await invokeEdgeFunction<ScanResult>('reminder-scan', {
        body: {},
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      message.success(
        t('reminders.scanDone', {
          events: data?.eventsCreated ?? 0,
          deliveries: data?.deliveriesCreated ?? 0,
        })
      );
    } catch (err) {
      message.error(err instanceof Error ? err.message : t('reminders.scanFailed'));
    } finally {
      setScanning(false);
    }
  };

  const openEdit = (row: ConfigRow) => {
    setEditing(row);
    const meta = typeOf(row);
    form.setFieldsValue({
      name_en: meta?.name_en ?? '',
      name_zh: meta?.name_zh ?? '',
      enabled: row.enabled,
      ahead_days: (row.ahead_days || [30]).join(','),
      interval_days: row.interval_days ?? meta?.default_interval_days ?? undefined,
      channels: row.channels || ['in_app_admin'],
      emails: (row.emails || []).join('\n'),
      phones: (row.phones || []).join('\n'),
    });
  };

  const typeLabel = (row: ConfigRow) => {
    const meta = typeOf(row);
    if (!meta) return row.type_code;
    return i18n.language === 'zh-CN' ? meta.name_zh : meta.name_en;
  };


  const handleSave = async () => {
    if (!editing || !canManage) return;
    const values = await form.validateFields();
    const ahead = String(values.ahead_days || '')
      .split(/[,，\s]+/)
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (ahead.length === 0) {
      message.error(t('reminders.aheadDaysRequired'));
      return;
    }
    const emails = String(values.emails || '')
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const phones = String(values.phones || '')
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const channels = (values.channels || []) as ReminderChannel[];
    if (channels.length === 0) {
      message.error(t('reminders.channelsRequired'));
      return;
    }
    const nameEn = String(values.name_en || '').trim();
    const nameZh = String(values.name_zh || '').trim();
    if (!nameEn || !nameZh) {
      message.error(t('reminders.nameRequired'));
      return;
    }

    setSavingCode(editing.type_code);
    try {
      const { error: typeErr } = await supabase
        .from('reminder_types')
        .update({
          name_en: nameEn,
          name_zh: nameZh,
        })
        .eq('code', editing.type_code);
      if (typeErr) throw typeErr;

      const { error } = await supabase
        .from('reminder_configs')
        .update({
          enabled: !!values.enabled,
          ahead_days: ahead,
          interval_days:
            typeOf(editing)?.rule_kind === 'interval_ahead'
              ? Number(values.interval_days) || typeOf(editing)?.default_interval_days || 90
              : null,
          channels,
          emails,
          phones,
          updated_at: new Date().toISOString(),
        })
        .eq('type_code', editing.type_code);
      if (error) throw error;
      message.success(t('reminders.saved'));
      setEditing(null);
      await loadConfigs();
    } catch (err) {
      message.error(err instanceof Error ? err.message : t('reminders.saveFailed'));
    } finally {
      setSavingCode(null);
    }
  };

  const openSourceEditor = (row?: ReminderSourceFieldRow) => {
    setEditingSource(row ?? null);
    if (row) {
      sourceForm.setFieldsValue({
        ...row,
        select_extra: (row.select_extra || []).join(', '),
        rule_kinds: row.rule_kinds || ['ahead_days'],
      });
    } else {
      sourceForm.resetFields();
      sourceForm.setFieldsValue({
        category: 'fleet',
        subject_kind: 'fleet_operator',
        table_name: 'fleet_operators',
        rule_kinds: ['ahead_days'],
        driver_in_app: false,
        enabled: true,
        sort_order: 100 + sourceFields.length * 10,
        select_extra: 'name',
      });
    }
    setSourceModalOpen(true);
  };

  const ensureReminderTypeForSource = async (args: {
    source_field: string;
    category: ReminderCategory;
    label_en: string;
    label_zh: string;
    rule_kinds: ReminderRuleKind[];
    default_interval_days: number | null;
    sort_order: number;
  }) => {
    const ruleKind = (args.rule_kinds[0] || 'ahead_days') as ReminderRuleKind;
    const interval =
      ruleKind === 'interval_ahead' ? args.default_interval_days || 90 : null;

    const { data: existing } = await supabase
      .from('reminder_types')
      .select('code')
      .eq('source_field', args.source_field)
      .limit(1)
      .maybeSingle();

    if (existing?.code) {
      const { error } = await supabase
        .from('reminder_types')
        .update({
          category: args.category,
          rule_kind: ruleKind,
          default_interval_days: interval,
        })
        .eq('code', existing.code);
      if (error) throw error;
      return { code: existing.code as string, created: false };
    }

    const code = typeCodeFromSourceField(args.source_field);
    const { error: typeErr } = await supabase.from('reminder_types').insert({
      code,
      name_en: args.label_en,
      name_zh: args.label_zh,
      category: args.category,
      source_field: args.source_field,
      rule_kind: ruleKind,
      default_interval_days: interval,
      sort_order: args.sort_order,
      is_system: false,
    });
    if (typeErr) throw typeErr;
    const { error: cfgErr } = await supabase.from('reminder_configs').insert({
      type_code: code,
      enabled: false,
      ahead_days: [30],
      interval_days: interval,
      channels: ['in_app_admin'],
      emails: [],
      phones: [],
    });
    if (cfgErr) throw cfgErr;
    return { code, created: true };
  };

  const handleSaveSource = async () => {
    if (!canManage) return;
    const values = await sourceForm.validateFields();
    if (!isSafeSqlIdent(String(values.due_column))) {
      message.error(t('reminders.invalidIdent'));
      return;
    }
    if (values.next_due_column && !isSafeSqlIdent(String(values.next_due_column))) {
      message.error(t('reminders.invalidIdent'));
      return;
    }
    const selectExtra = String(values.select_extra || '')
      .split(/[,，\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (selectExtra.some((c) => !isSafeSqlIdent(c))) {
      message.error(t('reminders.invalidIdent'));
      return;
    }
    const ruleKinds = (values.rule_kinds || ['ahead_days']) as ReminderRuleKind[];
    if (ruleKinds.length === 0) {
      message.error(t('reminders.ruleKindMismatch'));
      return;
    }
    const payload: Partial<ReminderSourceFieldRow> = {
      source_field: String(values.source_field).trim(),
      category: values.category,
      subject_kind: values.subject_kind,
      table_name: values.table_name,
      due_column: String(values.due_column).trim(),
      select_extra: selectExtra,
      rule_kinds: ruleKinds,
      default_interval_days: ruleKinds.includes('interval_ahead')
        ? Number(values.default_interval_days) || null
        : null,
      next_due_column: values.next_due_column
        ? String(values.next_due_column).trim()
        : null,
      driver_in_app: !!values.driver_in_app,
      label_en: String(values.label_en).trim(),
      label_zh: String(values.label_zh).trim(),
      enabled: !!values.enabled,
      sort_order: Number(values.sort_order) || 0,
      updated_at: new Date().toISOString(),
    };
    setSavingSource(true);
    try {
      if (editingSource) {
        const { source_field: _sf, ...update } = payload;
        const { error } = await supabase
          .from('reminder_source_fields')
          .update(update)
          .eq('source_field', editingSource.source_field);
        if (error) throw error;
        await ensureReminderTypeForSource({
          source_field: editingSource.source_field,
          category: payload.category as ReminderCategory,
          label_en: payload.label_en!,
          label_zh: payload.label_zh!,
          rule_kinds: ruleKinds,
          default_interval_days: payload.default_interval_days ?? null,
          sort_order: payload.sort_order || 0,
        });
      } else {
        const { error } = await supabase.from('reminder_source_fields').insert(payload);
        if (error) throw error;
        await ensureReminderTypeForSource({
          source_field: payload.source_field!,
          category: payload.category as ReminderCategory,
          label_en: payload.label_en!,
          label_zh: payload.label_zh!,
          rule_kinds: ruleKinds,
          default_interval_days: payload.default_interval_days ?? null,
          sort_order: payload.sort_order || 0,
        });
      }
      message.success(t('reminders.sourceSavedWithType'));
      setSourceModalOpen(false);
      await Promise.all([loadSources(), loadConfigs()]);
    } catch (err) {
      message.error(err instanceof Error ? err.message : t('reminders.sourceSaveFailed'));
    } finally {
      setSavingSource(false);
    }
  };

  const handleDeleteSource = (row: ReminderSourceFieldRow) => {
    localeConfirm(t, {
      title: t('reminders.deleteSourceTitle'),
      content: t('reminders.deleteSourceBodyMerged', { field: row.source_field }),
      okText: t('common.delete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        const { data: linkedTypes, error: listErr } = await supabase
          .from('reminder_types')
          .select('code')
          .eq('source_field', row.source_field);
        if (listErr) {
          message.error(formatAdminDbError(listErr, i18n.language, 'delete'));
          return;
        }
        const codes = (linkedTypes || []).map((x) => x.code);
        if (codes.length > 0) {
          const { count } = await supabase
            .from('reminder_events')
            .select('id', { count: 'exact', head: true })
            .in('type_code', codes)
            .eq('status', 'open');
          if ((count || 0) > 0) {
            message.error(t('reminders.deleteSourceBlockedOpen'));
            return;
          }
          const { error: delTypeErr } = await supabase
            .from('reminder_types')
            .delete()
            .in('code', codes);
          if (delTypeErr) {
            message.error(formatAdminDbError(delTypeErr, i18n.language, 'delete'));
            return;
          }
        }
        const { error } = await supabase
          .from('reminder_source_fields')
          .delete()
          .eq('source_field', row.source_field);
        if (error) {
          message.error(formatAdminDbError(error, i18n.language, 'delete'));
          return;
        }
        message.success(t('reminders.sourceDeleted'));
        if (editing && codes.includes(editing.type_code)) setEditing(null);
        await Promise.all([loadSources(), loadConfigs()]);
      },
    });
  };

  const handleDeleteType = (row: ConfigRow) => {
    const meta = typeOf(row);
    if (!meta) return;
    localeConfirm(t, {
      title: t('reminders.deleteTypeTitle'),
      content: t('reminders.deleteTypeBody', { name: typeLabel(row) }),
      okText: t('common.delete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        const { count } = await supabase
          .from('reminder_events')
          .select('id', { count: 'exact', head: true })
          .eq('type_code', row.type_code)
          .eq('status', 'open');
        if ((count || 0) > 0) {
          message.error(t('reminders.deleteBlockedOpen'));
          return;
        }
        const { error } = await supabase.from('reminder_types').delete().eq('code', row.type_code);
        if (error) {
          message.error(formatAdminDbError(error, i18n.language, 'delete'));
          return;
        }
        message.success(t('reminders.typeDeleted'));
        if (editing?.type_code === row.type_code) setEditing(null);
        await loadConfigs();
      },
    });
  };

  const configColumns: TableProps<ConfigRow>['columns'] = useMemo(
    () => [
      {
        title: t('reminders.columns.type'),
        key: 'type',
        render: (_, row) => typeLabel(row),
      },
      {
        title: t('reminders.columns.enabled'),
        dataIndex: 'enabled',
        width: 100,
        render: (v: boolean) =>
          v ? <Tag color="success">{t('reminders.on')}</Tag> : <Tag>{t('reminders.off')}</Tag>,
      },
      {
        title: t('reminders.columns.aheadDays'),
        dataIndex: 'ahead_days',
        width: 140,
        render: (v: number[]) => (v?.length ? v.join(', ') : '—'),
      },
      {
        title: t('reminders.columns.intervalDays'),
        dataIndex: 'interval_days',
        width: 110,
        render: (v: number | null | undefined, row) =>
          typeOf(row)?.rule_kind === 'interval_ahead' ? v ?? '—' : '—',
      },
      {
        title: t('reminders.columns.channels'),
        dataIndex: 'channels',
        render: (v: string[]) =>
          (v || []).map((c) => (
            <Tag key={c}>{t(`reminders.channels.${c}`, { defaultValue: c })}</Tag>
          )),
      },
      {
        title: t('reminders.columns.actions'),
        key: 'actions',
        width: 160,
        render: (_, row) => (
          <Space>
            <Button type="link" onClick={() => openEdit(row)}>
              {canManage ? t('reminders.edit') : t('reminders.view')}
            </Button>
            {canManage ? (
              <Button type="link" danger onClick={() => handleDeleteType(row)}>
                {t('common.delete')}
              </Button>
            ) : null}
          </Space>
        ),
      },
    ],
    [t, i18n.language, canManage, rows]
  );

  return (
    <AdminListPage
      title={t('reminders.title')}
      subtitle={t('reminders.subtitle')}
      widthMode="fluid"
      extra={
        canManage ? (
          <Button type="primary" loading={scanning} onClick={() => void runScan()}>
            {scanning ? t('reminders.scanRunning') : t('reminders.runScan')}
          </Button>
        ) : undefined
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('reminders.customTypesNotice')}
      />

      <Tabs
        items={[
          {
            key: 'configs',
            label: t('reminders.tabs.configs'),
            children: (
              <>
                <Table
                  rowKey="type_code"
                  loading={loading}
                  dataSource={rows}
                  columns={configColumns}
                  pagination={false}
                  scroll={{ x: 900 }}
                />

                {editing && (
                  <div
                    className="driver-card"
                    style={{
                      marginTop: 16,
                      padding: 16,
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      background: '#fafafa',
                    }}
                  >
                    <Text strong>
                      {t('reminders.editTitle')}: {typeLabel(editing)}
                    </Text>
                    <Paragraph type="secondary" style={{ marginTop: 4 }}>
                      {typeOf(editing)?.source_field}
                    </Paragraph>
                    <Form form={form} layout="vertical" disabled={!canManage} style={{ marginTop: 12 }}>
                      <Form.Item
                        name="name_zh"
                        label={t('reminders.fields.nameZh')}
                        rules={[{ required: true, message: t('reminders.nameRequired') }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        name="name_en"
                        label={t('reminders.fields.nameEn')}
                        rules={[{ required: true, message: t('reminders.nameRequired') }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item name="enabled" label={t('reminders.fields.enabled')} valuePropName="checked">
                        <Switch />
                      </Form.Item>
                      <Form.Item
                        name="ahead_days"
                        label={t('reminders.fields.aheadDays')}
                        extra={t('reminders.fields.aheadDaysHint')}
                        rules={[{ required: true }]}
                      >
                        <Input placeholder="30, 7" />
                      </Form.Item>
                      {typeOf(editing)?.rule_kind === 'interval_ahead' && (
                        <Form.Item
                          name="interval_days"
                          label={t('reminders.fields.intervalDays')}
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={1} max={730} style={{ width: '100%' }} />
                        </Form.Item>
                      )}
                      <Form.Item
                        name="channels"
                        label={t('reminders.fields.channels')}
                        rules={[{ required: true }]}
                        extra={
                          typeOf(editing)?.source_field &&
                          !isDriverInAppSourceRow(sourceFields, typeOf(editing)!.source_field)
                            ? t('reminders.driverChannelHint')
                            : undefined
                        }
                      >
                        <Checkbox.Group
                          options={CHANNELS.map((c) => ({
                            value: c,
                            label: t(`reminders.channels.${c}`),
                            disabled:
                              c === 'in_app_driver' &&
                              !!typeOf(editing)?.source_field &&
                              !isDriverInAppSourceRow(sourceFields, typeOf(editing)!.source_field),
                          }))}
                        />
                      </Form.Item>
                      <Form.Item name="emails" label={t('reminders.fields.emails')} extra={t('reminders.fields.emailsHint')}>
                        <Input.TextArea rows={3} placeholder="ops@example.com" />
                      </Form.Item>
                      <Form.Item name="phones" label={t('reminders.fields.phones')} extra={t('reminders.fields.phonesHint')}>
                        <Input.TextArea rows={3} placeholder="+61400000000" />
                      </Form.Item>
                      <Space>
                        <Button onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
                        {canManage && (
                          <Button
                            type="primary"
                            loading={savingCode === editing.type_code}
                            onClick={() => {
                              void handleSave();
                            }}
                          >
                            {t('common.confirm')}
                          </Button>
                        )}
                      </Space>
                    </Form>
                  </div>
                )}
              </>
            ),
          },
          {
            key: 'sources',
            label: t('reminders.tabs.sources'),
            children: (
              <>
                <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                  {t('reminders.sourcesHint')}
                </Paragraph>
                {canManage ? (
                  <Button
                    type="primary"
                    style={{ marginBottom: 12 }}
                    onClick={() => openSourceEditor()}
                  >
                    {t('reminders.addSource')}
                  </Button>
                ) : null}
                <Table
                  rowKey="source_field"
                  loading={sourcesLoading}
                  dataSource={sourceFields}
                  pagination={false}
                  scroll={{ x: 1100 }}
                  columns={[
                    {
                      title: t('reminders.sources.columns.field'),
                      dataIndex: 'source_field',
                      width: 280,
                    },
                    {
                      title: t('reminders.fields.category'),
                      dataIndex: 'category',
                      width: 90,
                      render: (c: string) => t(`reminders.categories.${c}`, { defaultValue: c }),
                    },
                    {
                      title: t('reminders.sources.columns.table'),
                      dataIndex: 'table_name',
                      width: 140,
                    },
                    {
                      title: t('reminders.sources.columns.dueColumn'),
                      dataIndex: 'due_column',
                      width: 140,
                    },
                    {
                      title: t('reminders.columns.enabled'),
                      dataIndex: 'enabled',
                      width: 90,
                      render: (v: boolean) =>
                        v ? <Tag color="success">{t('reminders.on')}</Tag> : <Tag>{t('reminders.off')}</Tag>,
                    },
                    {
                      title: t('reminders.sources.columns.label'),
                      key: 'label',
                      render: (_, row: ReminderSourceFieldRow) =>
                        i18n.language === 'zh-CN' ? row.label_zh : row.label_en,
                    },
                    {
                      title: t('reminders.columns.actions'),
                      key: 'actions',
                      width: 140,
                      render: (_, row: ReminderSourceFieldRow) =>
                        canManage ? (
                          <Space>
                            <Button type="link" onClick={() => openSourceEditor(row)}>
                              {t('reminders.edit')}
                            </Button>
                            <Button type="link" danger onClick={() => handleDeleteSource(row)}>
                              {t('common.delete')}
                            </Button>
                          </Space>
                        ) : null,
                    },
                  ]}
                />
              </>
            ),
          },
        ]}
      />

      <Modal
        title={editingSource ? t('reminders.editSourceTitle') : t('reminders.addSourceTitle')}
        open={sourceModalOpen}
        onCancel={() => setSourceModalOpen(false)}
        onOk={() => void handleSaveSource()}
        confirmLoading={savingSource}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
        width={640}
        destroyOnClose
      >
        <Form form={sourceForm} layout="vertical">
          <Form.Item
            name="source_field"
            label={t('reminders.sources.columns.field')}
            rules={[{ required: true }]}
            extra={t('reminders.sources.fieldHint')}
          >
            <Input disabled={!!editingSource} placeholder="fleet_operators.example_expiry" />
          </Form.Item>
          <Form.Item name="label_en" label={t('reminders.fields.nameEn')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="label_zh" label={t('reminders.fields.nameZh')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="category" label={t('reminders.fields.category')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'driver', label: t('reminders.categories.driver') },
                { value: 'vehicle', label: t('reminders.categories.vehicle') },
                { value: 'fleet', label: t('reminders.categories.fleet') },
              ]}
            />
          </Form.Item>
          <Form.Item name="subject_kind" label={t('reminders.sources.columns.subjectKind')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'driver', label: 'driver' },
                { value: 'vehicle', label: 'vehicle' },
                { value: 'fleet_operator', label: 'fleet_operator' },
              ]}
            />
          </Form.Item>
          <Form.Item name="table_name" label={t('reminders.sources.columns.table')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'drivers', label: 'drivers' },
                { value: 'vehicles', label: 'vehicles' },
                { value: 'fleet_operators', label: 'fleet_operators' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="due_column"
            label={t('reminders.sources.columns.dueColumn')}
            rules={[{ required: true }]}
          >
            <Input placeholder="public_liability_expiry" />
          </Form.Item>
          <Form.Item name="next_due_column" label={t('reminders.sources.columns.nextDue')}>
            <Input placeholder="optional" allowClear />
          </Form.Item>
          <Form.Item
            name="select_extra"
            label={t('reminders.sources.columns.selectExtra')}
            extra={t('reminders.sources.selectExtraHint')}
          >
            <Input placeholder="name, auth_uid" />
          </Form.Item>
          <Form.Item name="rule_kinds" label={t('reminders.fields.ruleKind')} rules={[{ required: true }]}>
            <Checkbox.Group
              options={[
                { value: 'ahead_days', label: t('reminders.ruleKinds.ahead_days') },
                { value: 'interval_ahead', label: t('reminders.ruleKinds.interval_ahead') },
              ]}
            />
          </Form.Item>
          <Form.Item name="default_interval_days" label={t('reminders.fields.intervalDays')}>
            <InputNumber min={1} max={730} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sort_order" label={t('reminders.sources.columns.sortOrder')}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="driver_in_app" label={t('reminders.sources.columns.driverInApp')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="enabled" label={t('reminders.fields.enabled')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </AdminListPage>
  );
}

export default ReminderSettings;
